<template>
  <b-modal
    :id="modalId"
    size="lg"
    no-close-on-backdrop
    no-close-on-esc
    no-fade
    no-stacking
    centered
    @shown="loadPaymentData"
    @hide="
      () => {
        gettingPaymentInfo = true
      }
    "
  >
    <template #modal-header>
      <b-row align-v="center">
        <b-col cols="auto" class="pr-0">
          <b-button
            variant="icon-button"
            @click="goToPreviousSubscriptionModal()"
            data-test-hook="back-button-payment-modal"
          >
            <i class="fas fa-chevron-left back-chevron-button" />
          </b-button>
        </b-col>
        <b-col>
          <h2 class="modal-title"><i class="fal fa-credit-card colorOrange mr-1" /> {{ $t('xPaymentInformation') }}</h2>
        </b-col>
      </b-row>
    </template>
    <template #modal-footer>
      <b-button
        v-if="useExistingBillingInfo"
        variant="primary"
        block
        @click="$v.$touch(), payForProduct()"
        :disabled="!canPay() || paying"
        data-test-hook="PayBtn"
      >
        {{ $t('xPayNow') }}
        <b-spinner class="ml-2" v-if="paying"></b-spinner>
      </b-button>
      <b-button
        v-else
        variant="outline-warning"
        block
        @click=";(savingAccountHolder = true), saveAccountHolder()"
        :disabled="savingAccountHolder"
        data-test-hook="confirm-billing-info-button"
      >
        {{ $t('xConfirmBillingInfo') }}
        <b-spinner class="ml-2" v-if="savingAccountHolder" />
      </b-button>
    </template>
    <b-alert :show="showCreditCardValidationBanner" variant="danger">
      <p>
        <i class="mr-2 fa-light fa-circle-exclamation"></i>
        {{ $t('xExperiencedTheseErrors') }}
      </p>
      <ul class="ml-2" v-for="(message, index) in errorMessages" :key="index">
        <li :id="index">
          {{ message }}
        </li>
      </ul>
    </b-alert>
    <div v-if="gettingPaymentInfo">
      <b-col class="float-center text-center">
        <b-spinner style="width: 10rem; height: 10rem;" variant="primary" label="Spinning"></b-spinner>
      </b-col>
    </div>
    <div v-else>
      <b-row>
        <b-col cols="12">
          <h2>
            <strong>{{ $t('xCreditCardInfo') }}</strong>
          </h2>
        </b-col>
      </b-row>
      <div v-if="existingPaymentInfo">
        <b-row v-if="!useExistingPaymentInfo" class="mb-2">
          <b-col cols="12">
            <p>{{ $t('xUpdatingPaymentWarning') }}</p>
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col data-test-hook="CreditCardCheckbox">
            <b-form-checkbox v-model="useExistingPaymentInfo">
              {{ $t('xUseCurrentCreditCardInformation') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
        <div v-if="useExistingPaymentInfo">
          <b-row class="mt-3">
            <b-col cols="12">
              <p>
                <strong>
                  <i class="fal fa-credit-card" />
                  {{ existingPaymentInfo.cardType }} -
                  {{ existingPaymentInfo.cardSuffix }}
                </strong>
              </p>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <p>
                {{ $t('xExpires') }}
                {{ existingPaymentInfo.expirationMonth + '/' + existingPaymentInfo.expirationYear }}
              </p>
            </b-col>
          </b-row>
        </div>
        <div v-else>
          <b-row>
            <b-col cols="12">
              <b-form-group id="credit-card-number">
                <b-form-input
                  v-model="$v.creditCardNumber.$model"
                  type="text"
                  maxlength="16"
                  v-mask="'################'"
                  :placeholder="$t('xCreditCardNumber')"
                  :state="
                    validState($v.creditCardNumber) == null || validState($v.creditCardNumber) == true ? null : false
                  "
                  aria-describedby="card-number-invalid"
                ></b-form-input>
                <b-form-invalid-feedback id="card-number-invalid">
                  {{ $t('xCCFieldValidation') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="4" sm="12">
              <b-form-group id="expiry-date-month">
                <b-form-input
                  v-model="$v.expirationMonth.$model"
                  type="text"
                  :placeholder="$t('xMm')"
                  maxlength="2"
                  v-mask="'##'"
                  :state="
                    validState($v.expirationMonth) == null || validState($v.expirationMonth) == true ? null : false
                  "
                  aria-describedby="expiry-month-invalid"
                ></b-form-input>
                <b-form-invalid-feedback id="expiry-month-invalid">
                  {{ $t('xExpirationMonthValidation') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col md="4" sm="12">
              <b-form-group id="expiry-date-year">
                <b-form-input
                  v-model="$v.expirationYear.$model"
                  type="text"
                  :placeholder="$t('xYy')"
                  maxlength="2"
                  v-mask="'##'"
                  :state="validState($v.expirationYear) == null || validState($v.expirationYear) == true ? null : false"
                  aria-describedby="expiry-year-invalid"
                ></b-form-input>
                <b-form-invalid-feedback id="expiry-year-invalid">
                  {{ $t('xExpirationYearValidation') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col md="4" sm="12">
              <b-input-group id="cvv">
                <b-form-input
                  v-model="$v.cvv.$model"
                  type="text"
                  v-mask="'####'"
                  maxlength="4"
                  :placeholder="$t('xCvv')"
                  :state="validState($v.cvv) == null || validState($v.cvv) == true ? null : false"
                  aria-describedby="cvv-invalid"
                ></b-form-input>
                <b-input-group-append>
                  <b-input-group-text>
                    <b-img :height="20" :src="require('@/assets/images/3-digit-code.svg')" />
                  </b-input-group-text>
                </b-input-group-append>
                <b-form-invalid-feedback id="cvv-invalid">
                  {{ $t('xCVVValidation') }}
                </b-form-invalid-feedback>
              </b-input-group>
            </b-col>
          </b-row>
        </div>
      </div>
      <div v-else>
        <b-row class="mb-2">
          <b-col cols="12">
            <p>{{ $t('xUpdatingPaymentWarning') }}</p>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12">
            <b-form-group id="credit-card-number">
              <b-form-input
                v-model="$v.creditCardNumber.$model"
                type="text"
                maxlength="16"
                v-mask="'################'"
                :placeholder="$t('xCreditCardNumber')"
                :state="
                  validState($v.creditCardNumber) == null || validState($v.creditCardNumber) == true ? null : false
                "
                aria-describedby="card-number-invalid"
              ></b-form-input>
              <b-form-invalid-feedback id="card-number-invalid">
                {{ $t('xCCFieldValidation') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4" sm="12">
            <b-form-group id="expiry-date-month">
              <b-form-input
                v-model="$v.expirationMonth.$model"
                type="text"
                :placeholder="$t('xMm')"
                maxlength="2"
                v-mask="'##'"
                :state="validState($v.expirationMonth) == null || validState($v.expirationMonth) == true ? null : false"
                aria-describedby="expiry-month-invalid"
              ></b-form-input>
              <b-form-invalid-feedback id="expiry-month-invalid">
                {{ $t('xExpirationMonthValidation') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="4" sm="12">
            <b-form-group id="expiry-date-year">
              <b-form-input
                v-model="$v.expirationYear.$model"
                type="text"
                :placeholder="$t('xYy')"
                maxlength="2"
                v-mask="'##'"
                :state="validState($v.expirationYear) == null || validState($v.expirationYear) == true ? null : false"
                aria-describedby="expiry-year-invalid"
              ></b-form-input>
              <b-form-invalid-feedback id="expiry-year-invalid">
                {{ $t('xExpirationYearValidation') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="4" sm="12">
            <b-input-group id="cvv">
              <b-form-input
                v-model="$v.cvv.$model"
                type="text"
                v-mask="'####'"
                maxlength="4"
                :placeholder="$t('xCvv')"
                :state="validState($v.cvv) == null || validState($v.cvv) == true ? null : false"
                aria-describedby="cvv-invalid"
              ></b-form-input>
              <b-input-group-append>
                <b-input-group-text>
                  <b-img :height="20" :src="require('@/assets/images/3-digit-code.svg')" id="ccCvvImage" />
                </b-input-group-text>
              </b-input-group-append>
              <b-form-invalid-feedback id="cvv-invalid">
                {{ $t('xCVVValidation') }}
              </b-form-invalid-feedback>
            </b-input-group>
          </b-col>
        </b-row>
      </div>
      <div class="mt-2" v-if="accountData">
        <b-row>
          <b-col>
            <h2>
              {{ $t('xBillingInformationLowercase') }}
            </h2>
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col>
            <b-form-checkbox
              v-model="useExistingBillingInfo"
              :disabled="savingAccountHolder"
              data-test-hook="payment-modal-checkbox-existing-info"
            >
              <p>{{ $t('xUseCurrentBillingInfo') }}</p>
            </b-form-checkbox>
          </b-col>
        </b-row>
        <b-row v-if="useExistingBillingInfo">
          <b-col cols="12">{{ accountProfile.firstName }} {{ accountProfile.lastName }}</b-col>
          <b-col cols="12">
            {{ DisplayAddress(accountProfile.address) }}
          </b-col>
        </b-row>
        <div v-if="!useExistingBillingInfo">
          <b-row>
            <b-col lg="6" md="6" sm="12">
              <b-form-group id="firstName-group">
                <b-form-input
                  class="form-control"
                  id="firstName"
                  v-model="$v.accountData.firstName.$model"
                  type="text"
                  :placeholder="$t('xFirstName')"
                  :state="
                    validState($v.accountData.firstName) == null || validState($v.accountData.firstName) == true
                      ? null
                      : false
                  "
                  aria-describedby="firstName-invalid"
                />
                <div class="invalid-feedback" id="firstName-invalid">
                  {{ $t('xRequiredField') }}
                </div>
              </b-form-group>
            </b-col>
            <b-col lg="6" md="6" sm="12">
              <b-form-group id="lastName-group">
                <b-form-input
                  class="form-control"
                  id="lastName"
                  v-model="$v.accountData.lastName.$model"
                  type="text"
                  :placeholder="$t('xLastName')"
                  :state="
                    validState($v.accountData.lastName) == null || validState($v.accountData.lastName) == true
                      ? null
                      : false
                  "
                  aria-describedby="lastName-invalid"
                />
                <div class="invalid-feedback" id="lastName-invalid">
                  {{ $t('xRequiredField') }}
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <PaymentModalTypeAheadAddress
                ref="typeAheadAddressBillingAddress"
                :currentAddress="accountData.address"
                @newAddress="setNewBillingAddress"
              />
            </b-col>
          </b-row>
        </div>
      </div>
      <div v-if="selectedProduct">
        <b-row class="my-3">
          <b-col cols="12">
            <div class="subscription-cost">
              <h3 class="mb-0">{{ $t('xTotalPayment') }}: {{ selectedProduct.cost.toFixed(2) | formatCurrency }}</h3>
              <p>
                <i>
                  {{ $t('xBilled') }}
                  <span v-if="selectedProduct.durationInMonths == 12">{{ $t('xYearly') }}</span>
                  <span v-else>{{ $t('xMonthly') }}</span>
                </i>
              </p>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-row>
        <b-col>
          <p>{{ $t('xUpdatingCCApplyActiveSubscriptions') }}</p>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <p>{{ $t('xThisSubscriptionWillAutoRenewShortVersion') }}</p>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <b-form-checkbox
            v-model="$v.consent.$model"
            :state="validState($v.consent)"
            data-test-hook="payment-modal-checkbox-consent"
          >
            {{ $t('xConsentCheckboxText') }}
          </b-form-checkbox>
          <p v-if="consent == false" class="colorRed ml-4">
            {{ $t('xConsentNotCheckedError') }}
          </p>
        </b-col>
      </b-row>
    </div>
    <div
      v-if="showSavingAccountHolderError"
      role="alert"
      class="alert alert--validation alert-danger"
      :show="showSavingAccountHolderError"
    >
      {{ $t('xFailedToUpdateBillingNameAndAddress') }}
    </div>
  </b-modal>
</template>

<script>
import PaymentModalTypeAheadAddress from '@/components/TypeAheadAddress'
import { required, requiredIf, minLength, maxLength, email, numeric } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import { isAddressValid } from '../../../utils/address'

const monthInRange = (value, vm) => value >= 1 && value <= 12

export default {
  components: { PaymentModalTypeAheadAddress },
  props: ['selectedProduct', 'accountProfile', 'modalId'],
  mixins: [validationMixin],
  emits: ['submit', 'goBack', 'savePremiumFeaturesGettingReadyInfo'],
  data() {
    return {
      consent: null,
      gettingPaymentInfo: true,
      paying: false,
      existingPaymentInfo: null,
      useExistingPaymentInfo: true, // CC info
      useExistingBillingInfo: true, // address, name, etc
      creditCardNumber: null,
      expirationMonth: null,
      expirationYear: null,
      cvv: null,
      showCreditCardValidationBanner: false,
      accountData: null,
      savingAccountHolder: false,
      showSavingAccountHolderError: false,
      addressSaveDisabled: false,
      errorMessages: []
    }
  },
  validations: {
    accountData: {
      firstName: {
        required,
        minLength: 1
      },
      lastName: {
        required,
        minLength: 1
      }
    },
    cvv: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(4),
      numeric
    },
    expirationMonth: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(2),
      numeric,
      monthInRange
    },
    expirationYear: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(2),
      numeric
    },
    creditCardNumber: {
      required,
      minLength: minLength(15),
      maxLength: maxLength(16),
      numeric
    },
    consent: {
      required,
      Boolean
    }
  },
  methods: {
    loadPaymentData() {
      this.getExistingBillingInfo()
    },
    getExistingBillingInfo() {
      this.gettingPaymentInfo = true
      this.$store
        .dispatch('getActivePaymentMethod', this.accountProfile.organizationId)
        .then(response => {
          this.existingPaymentInfo = response.data ? response.data : null
          this.gettingPaymentInfo = false
        })
        .catch(error => {
          this.existingPaymentInfo = null
          this.gettingPaymentInfo = false
        })
    },
    canPay() {
      if (this.existingPaymentInfo && this.useExistingPaymentInfo) {
        return true
      } else if (!this.useExistingPaymentInfo || !this.existingPaymentInfo) {
        if (!this.$v.$anyError && !this.paying) {
          return true
        } else return false
      } else return false
    },
    isAccountAddressValid() {
      return isAddressValid(this.accountData.address) && this.accountData.firstName && this.accountData.lastName
    },
    saveAccountHolder() {
      this.savingAccountHolder = true
      if (this.$refs?.typeAheadAddressBillingAddress) {
        this.$refs.typeAheadAddressBillingAddress.$v.$touch()
        this.$refs.typeAheadAddressBillingAddress.saveAttempted = true
      }

      if (!this.isAccountAddressValid(this.accountData.address)) {
        this.savingAccountHolder = false
        return
      }
      this.$store
        .dispatch('updateAccountHolder', {
          firstName: this.accountData.firstName,
          lastName: this.accountData.lastName,
          organizationAddress: {
            ...this.accountData.address,
            organizationId: this.accountData.organizationId
          }
        })
        .then(response => {
          this.showSavingAccountHolderError = false
          if (!this.isImpersonating) this.$store.dispatch('getUser')
          else this.$store.dispatch('getImpersonatedUser')
          this.$store.dispatch('getAccountProfile')
          this.timeTheAccountChange()
        })
        .catch(error => {
          this.showSavingAccountHolderError = true
          this.savingAccountHolder = false
        })
    },
    timeTheAccountChange() {
      setTimeout(
        function() {
          this.savingAccountHolder = false
          this.useExistingBillingInfo = true
        }.bind(this),
        5000
      )
    },
    goToPreviousSubscriptionModal() {
      this.$emit('goBack')
    },
    setNewBillingAddress(newAddress) {
      this.accountData.address = newAddress
      if (this.$refs.typeAheadAddressBillingAddress.$v.$anyError) {
        this.addressSaveDisabled = true
      } else {
        this.addressSaveDisabled = false
      }
    },
    async payForProduct() {
      this.paying = true
      if (this.consent) {
        if (!this.useExistingPaymentInfo && this.$v.$anyError) return
        this.$store.dispatch('storeSubscriptionTermsConsent', this.accountProfile.organizationId)
        if (this.useExistingPaymentInfo && this.existingPaymentInfo) {
          this.emitSubmission()
        } else if (this.existingPaymentInfo && !this.useExistingPaymentInfo) {
          await this.removeAndAddBillingInfo()
          if (this.paying) {  // Check to make sure there was no issues with adding new billing info
            this.emitSubmission() 
          }
        } else {
          await this.saveBillingInfo()
        }
      } else {
        this.consent = false
        this.paying = false
      }
    },
    emitSubmission() {
      new Promise((resolve, reject) => {
        this.$emit('submit', { resolve, reject })
      })
        .then(() => {
          this.paying = false
          this.showCreditCardValidationBanner = false
          this.errorMessages = []
          this.$emit('savePremiumFeaturesGettingReadyInfo')
          this.$bvModal.hide(this.modalId)
        })
        .catch(errorMessage => {
          this.paying = false
          this.errorMessages = []
          if (errorMessage) {
            this.errorMessages.push(errorMessage)
          } else {
            this.errorMessages.push(this.$t('xProblemProcessingPayment'))
          }
          this.showCreditCardValidationBanner = true
        })
    },
    async saveBillingInfo() {
      // no existing payment info, add new cc
      let addPaymentModel = {
        organizationId: this.accountProfile.organizationId,
        cardNumber: this.creditCardNumber,
        cvv: this.cvv,
        expirationMonth: this.expirationMonth,
        expirationYear: '20' + this.expirationYear
      }
      await this.$store
        .dispatch('addBillingAccountPaymentMethod', addPaymentModel)
        .then(response => {
          this.emitSubmission()
        })
        .catch(error => {
          this.paying = false
          this.handleErrors(error)
        })
    },
    async removeAndAddBillingInfo() {
      if (this.existingPaymentInfo && !this.useExistingPaymentInfo) {
        //delete/cancel/remove existing payment info,
        //add new user entered credit card info to account,
        //add apparatus to the selected product, then proceed to success page (if all calls succeed)
        let removePaymentModel = {
          organizationId: this.accountProfile.organizationId,
          paymentId: this.existingPaymentInfo.id
        }
        let addPaymentModel = {
          organizationId: this.accountProfile.organizationId,
          cardNumber: this.creditCardNumber,
          cvv: this.cvv,
          expirationMonth: this.expirationMonth,
          expirationYear: '20' + this.expirationYear
        }
        await this.$store
          .dispatch('addBillingAccountPaymentMethod', addPaymentModel)
          .then(response => {
            this.$store
              .dispatch('removeBillingAccountPaymentMethod', removePaymentModel)
              .then(response => {})
              .catch(error => {
                this.errorMessages.push(this.$t('xFailedToAddPaymentMethod'))
                this.showCreditCardValidationBanner = true
              })
          })
          .catch(error => {
            this.paying = false
            this.handleErrors(error)
          })
      }
    },
    handleErrors(error) {
      this.gettingPaymentInfo = false
      this.errorMessages = []
      if (error.response.data.errors) {
        if (error.response.data.errors.CardNumber || error.response.data.errors.cardNumber) {
          this.showCreditCardValidationBanner = true
          this.errorMessages.push(this.$t('xCCIsInvalid'))
        }
        if (error.response.data.errors.Cvv || error.response.data.errors.cvv) {
          this.showCreditCardValidationBanner = true
          this.errorMessages.push(this.$t('xCvvInvalid'))
        }
        if (error.response.data.errors.ExpirationMonth || error.response.data.errors.expirationMonth) {
          this.showCreditCardValidationBanner = true
          this.errorMessages.push(this.$t('xExpirationMonthInvalid'))
        }
        if (error.response.data.errors.ExpirationYear || error.response.data.errors.expirationYear) {
          this.showCreditCardValidationBanner = true
          this.errorMessages.push(this.$t('xExpirationYearInvalid'))
        }
        if (error.response.data.errors.billing || error.response.data.errors.Billing) {
          this.errorMessages.push(
            error.response.data.errors.billing ? error.response.data.errors.billing : error.data.response.errors.Billing
          )
          this.showCreditCardValidationBanner = true
        }
      } else {
        this.errorMessages.push(this.$t('xFailedToAddPaymentMethod'))
        this.showCreditCardValidationBanner = true
      }
    }
  },
  watch: {
    accountProfile: function(newVal, oldVal) {
      if (newVal) this.accountData = _.cloneDeep(newVal)
    }
  },
  mounted() {
    if (this.accountProfile) this.accountData = _.cloneDeep(this.accountProfile)
  }
}
</script>
